import React, { useEffect, useRef, useState } from "react";
import { Pin, PinOff, BellOff, Bell, Archive, ArchiveRestore, MailOpen, Mail, Trash2, Star, X, ChevronLeft, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ROW = "w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-[color:var(--yalola-surface-alt)] transition-colors";

// Feuille d'actions d'une conversation (appui long dans la liste ou menu ⋮ du chat) :
// épingler · sourdine (8 h / 1 semaine / toujours) · archiver · non lu · supprimer pour moi · ⭐ importants.
// `conv` = { id, other, pinned, muted, archived, manual_unread, unread }.
export const ConvActionsSheet = ({ conv, t, isMobile, onClose, onChanged, onStarred, onDeleted, onViewProfile }) => {
  const open = !!conv;
  const [view, setView] = useState("main"); // main | mute | delete
  const [busy, setBusy] = useState(false);
  const openedAt = useRef(0);
  useEffect(() => { if (open) { openedAt.current = Date.now(); setView("main"); setBusy(false); } }, [open, conv?.id]);
  // Le « clic fantôme » qui suit la fin d'un appui long tombe sur le fond : on l'ignore juste après l'ouverture.
  const onBackdrop = () => { if (Date.now() - openedAt.current < 600) return; onClose(); };
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;

  const name = conv.other?.name || "";
  const patch = async (payload, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/chat/conversations/${conv.id}/prefs`, payload);
      toast.success(msg || t("conv.saved", "Enregistré"));
      onChanged?.(data);
      onClose();
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const doDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete(`/chat/conversations/${conv.id}`);
      toast.success(t("conv.deleted", "Conversation supprimée"));
      onDeleted?.();
      onClose();
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const isUnread = !!conv.manual_unread || (conv.unread || 0) > 0;

  return (
    <div className="fixed inset-0 z-[97] flex items-end sm:items-center justify-center bg-black/50" data-testid="conv-actions-sheet" onClick={onBackdrop}>
      <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: isMobile ? "var(--sa-bottom)" : undefined }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          {view !== "main" && (
            <button onClick={() => setView("main")} className="h-8 w-8 -ml-2 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" data-testid="conv-actions-back"><ChevronLeft size={18} /></button>
          )}
          <div className="font-bold text-sm flex-1 truncate" style={{ color: "var(--yalola-text)" }} data-testid="conv-actions-title">
            {view === "mute" ? t("conv.mute", "Mettre en sourdine") : view === "delete" ? t("conv.delete", "Supprimer la conversation") : t("conv.actions_title", "Conversation avec {name}").replace("{name}", name)}
          </div>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-text-muted)" }} data-testid="conv-actions-close"><X size={18} /></button>
        </div>

        {view === "main" && (
          <div className="py-1" style={{ color: "var(--yalola-text)" }}>
            {onViewProfile && conv.other?.id && (
              <button onClick={() => { onClose(); onViewProfile(conv); }} className={ROW} data-testid="conv-action-profile"><UserRound size={18} /> {t("matching.view_profile", "Voir le profil")}</button>
            )}
            {onStarred && (
              <button onClick={() => { onClose(); onStarred(); }} className={ROW} data-testid="conv-action-starred"><Star size={18} style={{ color: "#E0A63C" }} fill="#E0A63C" /> {t("conv.starred", "Messages importants")}</button>
            )}
            <button onClick={() => patch({ pinned: !conv.pinned })} className={ROW} disabled={busy} data-testid="conv-action-pin">
              {conv.pinned ? <PinOff size={18} /> : <Pin size={18} />} {conv.pinned ? t("conv.unpin", "Désépingler") : t("conv.pin", "Épingler")}
            </button>
            {conv.muted ? (
              <button onClick={() => patch({ muted: "off" })} className={ROW} disabled={busy} data-testid="conv-action-unmute"><Bell size={18} /> {t("conv.unmute", "Réactiver les notifications")}</button>
            ) : (
              <button onClick={() => setView("mute")} className={ROW} data-testid="conv-action-mute"><BellOff size={18} /> {t("conv.mute", "Mettre en sourdine")}</button>
            )}
            <button onClick={() => patch({ archived: !conv.archived })} className={ROW} disabled={busy} data-testid="conv-action-archive">
              {conv.archived ? <ArchiveRestore size={18} /> : <Archive size={18} />} {conv.archived ? t("conv.unarchive", "Désarchiver") : t("conv.archive", "Archiver")}
            </button>
            <button onClick={() => patch({ unread: !isUnread })} className={ROW} disabled={busy} data-testid="conv-action-unread">
              {isUnread ? <MailOpen size={18} /> : <Mail size={18} />} {isUnread ? t("conv.mark_read", "Marquer comme lu") : t("conv.mark_unread", "Marquer comme non lu")}
            </button>
            <button onClick={() => setView("delete")} className={`${ROW} text-red-600`} data-testid="conv-action-delete"><Trash2 size={18} /> {t("conv.delete", "Supprimer la conversation")}</button>
          </div>
        )}

        {view === "mute" && (
          <div className="py-1" style={{ color: "var(--yalola-text)" }}>
            {[["8h", t("conv.mute_8h", "8 heures")], ["1w", t("conv.mute_1w", "1 semaine")], ["forever", t("conv.mute_forever", "Toujours")]].map(([v, label]) => (
              <button key={v} onClick={() => patch({ muted: v }, t("conv.muted_label", "En sourdine"))} className={ROW} disabled={busy} data-testid={`conv-mute-${v}`}><BellOff size={18} /> {label}</button>
            ))}
          </div>
        )}

        {view === "delete" && (
          <div className="p-4 space-y-3">
            <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="conv-delete-desc">{t("conv.delete_confirm", "Supprimer cette conversation ? Elle disparaît de ta boîte ; {name} garde ses messages.").replace("{name}", name)}</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setView("main")} className="px-3.5 py-1.5 rounded-full text-sm font-medium" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text)" }} data-testid="conv-delete-cancel">{t("common.cancel", "Annuler")}</button>
              <button onClick={doDelete} disabled={busy} className="px-3.5 py-1.5 rounded-full text-sm font-medium text-white" style={{ background: "#e11d48" }} data-testid="conv-delete-confirm">{t("chat.delete", "Supprimer")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
